import React, { useState, useEffect, useCallback } from 'react';
import { NamedCollection, Slide } from '../types';
import { collectionCreatorLabel, collectionPermissions } from '../utils/collectionPermissions';
import { slideApiService } from '../services/slideApiService';
import { useShop } from '../contexts/ShopContext';
import AuthenticatedImage from './AuthenticatedImage';
import NamePromptModal from './NamePromptModal';
import ConfirmModal from './ConfirmModal';
import AddFavoritesToCollectionModal from './AddFavoritesToCollectionModal';
import ShareCollectionModal from './ShareCollectionModal';
import FavoriteButton from './FavoriteButton';

/**
 * Named Collections management for the My Shop tab. Available to all internal
 * members. Mirrors the iOS MyCollectionsView (list) + CollectionDetailView (detail):
 * list / create-empty / delete, and per-collection slide view / remove / rename / delete.
 *
 * Lists the user's own collections plus other members' shop-visible ones (scope=all). Every
 * action button is shown from the server-computed `permissions` (collectionPermissions), so the
 * role matrix is never re-derived here. See slide-svc/SHOP_VISIBLE_COLLECTIONS_PLAN.md.
 */
interface CollectionsTabProps {
  shopId: string;
}

const PRIMARY = 'var(--text-accent)';
const BORDER = 'var(--border-light)';
const MUTED = 'var(--text-accent)';
const TEXT = 'var(--text-accent)';

/** Small pill flagging a collection that is currently shared as a feed cube. */
const SharedBadge: React.FC = () => (
  <span
    title="Shared with client/vendor users as a feed cube"
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1,
      color: PRIMARY, backgroundColor: 'var(--bg-accent)',
      padding: '3px 8px', borderRadius: '10px', whiteSpace: 'nowrap',
    }}
  >
    Shared
  </span>
);

/**
 * "Group of people" icon (the widely used Feather/Lucide "users" glyph, drawn inline because the
 * SPA has no icon library) flagging a collection that is visible to everyone in the shop.
 * Icon only: the tooltip and aria-label carry the meaning.
 */
const ShopVisibleBadge: React.FC = () => (
  <span
    title="Visible to everyone in the shop"
    aria-label="Visible to everyone in the shop"
    role="img"
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      color: PRIMARY, border: `1px solid ${BORDER}`,
      padding: '2px 7px', borderRadius: '10px', lineHeight: 1,
    }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  </span>
);

/**
 * Delete-confirmation copy. When the collection is currently shared, warn that deleting also
 * stops the share (slide-svc cascade-unshares on delete). When it is shop visible, warn that it
 * disappears for everyone in the shop, not just for you.
 */
const deleteConfirmMessage = (collection: NamedCollection): string => {
  const parts = [`Delete collection "${collection.name}"?`];
  if (collection.shopVisible) {
    parts.push('It is visible to everyone in the shop, so it will be deleted for everyone.');
  }
  if (collection.shared) {
    parts.push('It is currently shared, so deleting it will also stop sharing its slides with the people it was shared with.');
  }
  parts.push('This cannot be undone.');
  return parts.join(' ');
};

/** Confirmation copy for changing a collection's visibility. */
const visibilityConfirmMessage = (collection: NamedCollection, makeShopVisible: boolean): string =>
  makeShopVisible
    ? `Make "${collection.name}" visible to everyone in the shop? Members will be able to view it, and members with edit access will be able to change it.`
    : `Make "${collection.name}" private? Only you will be able to see it. Other members will lose access.`;

// Collection-preview thumbnail stack (mirrors the iOS MyCollectionsView preview)
const PREVIEW_COUNT = 3;       // max thumbnails per card
const PREVIEW_TILE = 44;       // square thumbnail size (px)
const PREVIEW_PEEK = 0.45;     // fraction of each lower tile that peeks out; lower = tighter overlap

const CollectionsTab: React.FC<CollectionsTabProps> = ({ shopId }) => {
  const { userRole } = useShop();
  const [collections, setCollections] = useState<NamedCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [previews, setPreviews] = useState<Record<string, Slide[]>>({});
  const [pendingDelete, setPendingDelete] = useState<NamedCollection | null>(null);

  const loadCollections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await slideApiService.listCollections(shopId);
      setCollections(result);
    } catch (err) {
      console.error('Failed to load collections:', err);
      setError('Failed to load collections.');
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    setSelectedId(null);
    loadCollections();
  }, [loadCollections]);

  // Hydrate the first-N slides of every collection in ONE combined batch (deduped, distributed
  // back per collection) — not N+1. Runs whenever the list changes; image cache makes it cheap.
  const loadPreviews = useCallback(async (cols: NamedCollection[]) => {
    const wanted: string[] = [];
    const seen = new Set<string>();
    for (const c of cols) {
      for (const id of c.slideRefs.slice(0, PREVIEW_COUNT)) {
        if (!seen.has(id)) { seen.add(id); wanted.push(id); }
      }
    }
    if (wanted.length === 0) { setPreviews({}); return; }
    try {
      const slides = await slideApiService.batchGetSlides(shopId, wanted);
      const byId = new Map(slides.map(s => [s.id, s]));
      const map: Record<string, Slide[]> = {};
      for (const c of cols) {
        map[c.id] = c.slideRefs
          .slice(0, PREVIEW_COUNT)
          .map(id => byId.get(id))
          .filter((s): s is Slide => s !== undefined);
      }
      setPreviews(map);
    } catch (err) {
      console.error('Failed to load collection previews:', err);
    }
  }, [shopId]);

  useEffect(() => {
    if (collections.length > 0) loadPreviews(collections);
  }, [collections, loadPreviews]);

  const handleCreate = async (name: string) => {
    const created = await slideApiService.createCollection(shopId, name);
    setShowCreate(false);
    setCollections(prev => [created, ...prev]);
  };

  // Confirmed via ConfirmModal; a failure throws, which the modal shows inline (it stays open).
  const confirmDelete = async (collection: NamedCollection) => {
    await slideApiService.deleteCollection(shopId, collection.id);
    setCollections(prev => prev.filter(c => c.id !== collection.id));
    setPendingDelete(null);
  };

  // Detail view
  if (selectedId) {
    return (
      <CollectionDetail
        shopId={shopId}
        collectionId={selectedId}
        onBack={() => setSelectedId(null)}
        onChanged={loadCollections}
        onDeleted={() => { setSelectedId(null); loadCollections(); }}
      />
    );
  }

  // List view
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: TEXT }}>Collections</h2>
          <p style={{ margin: '4px 0 0', color: MUTED, fontSize: '0.875rem' }}>
            Your collections, plus collections other members have made visible to the shop.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + New Collection
        </button>
      </div>

      {error && (
        <div className="error-banner card mb-6"><div className="text-error">{error}</div></div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: MUTED }}>
          <div className="spinner-sm mx-auto mb-2" />
          Loading collections…
        </div>
      ) : collections.length === 0 ? (
        <div className="empty-state card text-center py-12">
          <h3 className="heading-secondary text-xl mb-2">No Collections Yet</h3>
          <p className="text-muted">
            Create one here, or favorite slides on the Slides tab and save them as a collection.
          </p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '12px',
          }}
        >
          {collections.map(collection => {
            const perms = collectionPermissions(collection, userRole);
            const creator = collectionCreatorLabel(collection);
            return (
            <div
              key={collection.id}
              onClick={() => setSelectedId(collection.id)}
              style={{
                position: 'relative',
                padding: '14px 16px',
                backgroundColor: 'var(--bg-card)',
                border: `1px solid ${BORDER}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = PRIMARY; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; }}
            >
              {/* name truncates; right padding leaves room for the delete button */}
              <div
                title={collection.name}
                style={{ fontWeight: 600, color: TEXT, paddingRight: perms.canDelete ? '28px' : 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {collection.name}
              </div>
              <div style={{ fontSize: '0.8125rem', color: MUTED, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span>{collection.slideCount} {collection.slideCount === 1 ? 'slide' : 'slides'}</span>
                {collection.shopVisible && <ShopVisibleBadge />}
                {collection.shared && <SharedBadge />}
              </div>
              {creator && (
                <div style={{ fontSize: '0.75rem', color: MUTED, marginTop: '2px' }}>by {creator}</div>
              )}
              <CollectionThumbStack shopId={shopId} slides={previews[collection.id] || []} total={collection.slideCount} />
              {perms.canDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); setPendingDelete(collection); }}
                title="Delete collection"
                style={{
                  position: 'absolute', top: '8px', right: '8px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: MUTED, fontSize: '1rem', padding: '4px 6px', borderRadius: '4px', lineHeight: 1,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--error-text)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
              >
                🗑
              </button>
              )}
            </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <NamePromptModal
          title="New Collection"
          confirmLabel="Create"
          onCancel={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Delete Collection"
          message={deleteConfirmMessage(pendingDelete)}
          confirmLabel="Delete"
          destructive
          onConfirm={() => confirmDelete(pendingDelete)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

// ---- Collection list-row preview: overlapping thumbnail stack ----

/**
 * Overlapping "stack" of the first few slide thumbnails (first slide on top, lower ones peek
 * ~PREVIEW_PEEK), with a "+N" when the collection has more. Mirrors the iOS MyCollectionsView
 * preview. Overlap via negative margin-left; flex items honor z-index without position:relative.
 */
const CollectionThumbStack: React.FC<{ shopId: string; slides: Slide[]; total: number }> = ({ shopId, slides, total }) => {
  if (slides.length === 0) return null;
  const overlap = PREVIEW_TILE * (1 - PREVIEW_PEEK);
  const extra = total - slides.length;
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
      <div style={{ display: 'flex' }}>
        {slides.map((slide, idx) => {
          const imageId = slide.primaryImage?.id || slide.images?.[0]?.id;
          return (
            <div
              key={slide.id}
              style={{
                width: PREVIEW_TILE,
                height: PREVIEW_TILE,
                marginLeft: idx === 0 ? 0 : -overlap,
                zIndex: slides.length - idx, // first slide on top, fully visible
                borderRadius: 6,
                overflow: 'hidden',
                border: '1.5px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                backgroundColor: 'var(--bg-secondary)',
                flexShrink: 0,
              }}
            >
              {imageId && (
                <AuthenticatedImage
                  shopId={shopId}
                  slideId={slide.id}
                  imageId={imageId}
                  alt={slide.name}
                  size="thumb_300"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
            </div>
          );
        })}
      </div>
      {extra > 0 && (
        <span style={{ marginLeft: '8px', fontSize: '0.75rem', fontWeight: 600, color: MUTED }}>+{extra}</span>
      )}
    </div>
  );
};

// ---- Detail view ----

interface CollectionDetailProps {
  shopId: string;
  collectionId: string;
  onBack: () => void;
  onChanged: () => void;   // refresh list (e.g. slide count, name)
  onDeleted: () => void;
}

const CollectionDetail: React.FC<CollectionDetailProps> = ({ shopId, collectionId, onBack, onChanged, onDeleted }) => {
  const [collection, setCollection] = useState<NamedCollection | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRename, setShowRename] = useState(false);
  const [showAddFavorites, setShowAddFavorites] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'delete' | 'visibility' | null>(null);

  // Every action is gated on the server-computed permissions (creator, shop visibility and role
  // are all folded in server-side). Sharing is creator-only + OWNER/ADMIN/PLATFORM_ADMIN (C1, D5).
  const { userRole } = useShop();
  const perms = collection ? collectionPermissions(collection, userRole) : null;
  const creator = collection ? collectionCreatorLabel(collection) : null;

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const col = await slideApiService.getCollection(shopId, collectionId);
      setCollection(col);
      const hydrated = col.slideRefs.length > 0
        ? await slideApiService.batchGetSlides(shopId, col.slideRefs)
        : [];
      setSlides(hydrated);
    } catch (err) {
      console.error('Failed to load collection:', err);
      setError('Failed to load collection.');
    } finally {
      setLoading(false);
    }
  }, [shopId, collectionId]);

  useEffect(() => { load(); }, [load]);

  const handleRename = async (name: string) => {
    const updated = await slideApiService.renameCollection(shopId, collectionId, name);
    setCollection(updated);
    setShowRename(false);
    onChanged();
  };

  // Both are confirmed via ConfirmModal; a failure throws, which the modal shows inline.
  const confirmDelete = async () => {
    await slideApiService.deleteCollection(shopId, collectionId);
    setPendingAction(null);
    onDeleted();
  };

  const confirmToggleVisibility = async () => {
    if (!collection) return;
    const makeShopVisible = !collection.shopVisible;
    const updated = await slideApiService.setCollectionVisibility(shopId, collectionId, makeShopVisible);
    setCollection(updated);
    setPendingAction(null);
    onChanged();
    showToast(makeShopVisible ? 'Collection is now visible to the shop' : 'Collection is now private');
  };

  const handleRemoveSlide = async (slideId: string) => {
    try {
      const updated = await slideApiService.removeSlideFromCollection(shopId, collectionId, slideId);
      setCollection(updated);
      setSlides(prev => prev.filter(s => s.id !== slideId));
      onChanged();
    } catch (err) {
      console.error('Failed to remove slide:', err);
      showToast('Failed to remove slide from collection.');
    }
  };

  return (
    <div>
      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', color: PRIMARY, cursor: 'pointer', fontSize: '0.875rem', padding: '4px 0', marginBottom: '12px' }}
      >
        ← Back to Collections
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: TEXT, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span>{collection?.name || 'Collection'}</span>
            {collection?.shopVisible && <ShopVisibleBadge />}
            {collection?.shared && <SharedBadge />}
          </h2>
          <p style={{ margin: '4px 0 0', color: MUTED, fontSize: '0.875rem' }}>
            {collection ? `${collection.slideCount} ${collection.slideCount === 1 ? 'slide' : 'slides'}` : ''}
            {creator ? ` · Created by ${creator}` : ''}
          </p>
        </div>
        {collection && perms && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {perms.canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddFavorites(true)}>+ Add Favorites</button>
            )}
            {perms.canShare && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowShare(true)}
                disabled={slides.length === 0}
                title={slides.length === 0 ? 'Add slides before sharing' : 'Share this collection with clients'}
              >
                Share
              </button>
            )}
            {perms.canChangeVisibility && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setPendingAction('visibility')}
                title={collection.shopVisible ? 'Only you will be able to see this collection' : 'Let everyone in the shop see this collection'}
              >
                {collection.shopVisible ? 'Make Private' : 'Make Shop Visible'}
              </button>
            )}
            {perms.canEdit && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowRename(true)}>Rename</button>
            )}
            {perms.canDelete && (
              <button className="btn btn-ghost btn-sm" onClick={() => setPendingAction('delete')} style={{ color: 'var(--error-text)' }}>Delete</button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="error-banner card mb-6"><div className="text-error">{error}</div></div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: MUTED }}>
          <div className="spinner-sm mx-auto mb-2" />
          Loading slides…
        </div>
      ) : slides.length === 0 ? (
        <div className="empty-state card text-center py-12">
          <h3 className="heading-secondary text-xl mb-2">No Slides in This Collection</h3>
          {perms?.canEdit && (
            <>
              <p className="text-muted mb-4">Add your favorited slides to this collection.</p>
              <button className="btn btn-primary" onClick={() => setShowAddFavorites(true)}>Add from Favorites</button>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
          {slides.map(slide => {
            const imageId = slide.primaryImage?.id || slide.images?.[0]?.id;
            return (
              <div
                key={slide.id}
                style={{ position: 'relative', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden', backgroundColor: 'var(--bg-card)' }}
              >
                <div style={{ aspectRatio: '1 / 1', backgroundColor: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {imageId ? (
                    <AuthenticatedImage
                      shopId={shopId}
                      slideId={slide.id}
                      imageId={imageId}
                      alt={slide.name}
                      size="thumb_300"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ color: MUTED, fontSize: '0.75rem' }}>No image</span>
                  )}
                </div>
                {/* Favorite star (bottom-right) to match the search compact card; no name label. */}
                <FavoriteButton slideId={slide.id} />
                {perms?.canEdit && (
                <button
                  onClick={() => handleRemoveSlide(slide.id)}
                  title="Remove from collection"
                  style={{
                    position: 'absolute', top: '6px', right: '6px',
                    width: '26px', height: '26px', borderRadius: '50%',
                    border: 'none', cursor: 'pointer',
                    backgroundColor: 'rgba(0,0,0,0.55)', color: 'white',
                    fontSize: '0.9rem', lineHeight: '26px', padding: 0,
                  }}
                >
                  ×
                </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showRename && collection && (
        <NamePromptModal
          title="Rename Collection"
          confirmLabel="Save"
          initialValue={collection.name}
          onCancel={() => setShowRename(false)}
          onSubmit={handleRename}
        />
      )}

      {showAddFavorites && collection && (
        <AddFavoritesToCollectionModal
          shopId={shopId}
          collectionId={collectionId}
          collectionName={collection.name}
          onAdded={(count) => {
            setShowAddFavorites(false);
            showToast(`Added ${count} to "${collection.name}"`);
            load();      // refresh this collection's slides + count
            onChanged(); // refresh the list (slide counts)
          }}
          onClose={() => setShowAddFavorites(false)}
        />
      )}

      {showShare && collection && (
        <ShareCollectionModal
          shopId={shopId}
          collectionId={collectionId}
          collectionName={collection.name}
          slides={slides}
          onShared={(message) => {
            setShowShare(false);
            showToast(message);
          }}
          onClose={() => setShowShare(false)}
        />
      )}

      {pendingAction === 'delete' && collection && (
        <ConfirmModal
          title="Delete Collection"
          message={deleteConfirmMessage(collection)}
          confirmLabel="Delete"
          destructive
          onConfirm={confirmDelete}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {pendingAction === 'visibility' && collection && (
        <ConfirmModal
          title={collection.shopVisible ? 'Make Collection Private' : 'Make Collection Shop Visible'}
          message={visibilityConfirmMessage(collection, !collection.shopVisible)}
          confirmLabel={collection.shopVisible ? 'Make Private' : 'Make Shop Visible'}
          onConfirm={confirmToggleVisibility}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {toast && (
        <div
          style={{
            position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
            backgroundColor: 'var(--bg-accent)', color: 'var(--text-primary)', padding: '12px 20px',
            borderRadius: '8px', fontSize: '0.875rem', zIndex: 1100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default CollectionsTab;
